/**
 * QueryBar — type a sentence, get a query you can see and correct.
 *
 * ─── IT DRIVES THE CONTROLS, IT DOES NOT REPLACE THEM ───
 * Submitting sets the page's filters. It does not run a hidden query of its own. That is the
 * whole design: whatever the bar understood is visible in the filter row a second later, so the
 * operator can fix one chip instead of rewording a sentence and hoping. A search box that
 * silently becomes the source of truth is a search box you cannot debug from the outside.
 *
 * It is also why this survives the arrival of neural search unchanged. Swap `parseQuery` for an
 * `await search.parseQuery(text)` and this component, the chip row and the controls all keep
 * working — the interface is "a parse", not "results".
 *
 * ─── IT APPLIES AS YOU TYPE ───
 * There is no "type it, then press the button" step. The sentence parses and writes the filters
 * on a short pause, so the chip row below is never describing an older question than the box
 * above it. That gap was the bug: an operator typed a new sentence, read the controls, and the
 * controls were still answering the previous one — two sources of truth on one screen, with
 * nothing on either to say which the results came from.
 *
 * `Ask` and Enter stay, and apply immediately. A pause is a guess about when somebody has
 * finished a thought, and it is wrong often enough that taking the affordance away would make
 * the page feel like it was waiting on nothing.
 *
 * EMPTYING THE BOX CLEARS THE QUERY, for the same symmetry: if typing is applying, then deleting
 * has to be un-applying, or the last sentence goes on filtering an empty search bar.
 *
 * ─── WHAT IT ADMITS TO ───
 * Two things are always shown, because both are ways a search can be confidently wrong:
 *   CORRECTIONS — "read *shire* as **shirt**". A fix the operator cannot see is a query they
 *                 did not write.
 *   UNREAD WORDS — a colour the estate does not collect, a room that is not a camera. Silence
 *                 here is the worst outcome: the filter is simply absent and the results look
 *                 like a valid answer to the question that was asked.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { ParsedQuery } from './queryParser';
import { QUERY_EXAMPLES } from './queryParser';
import { QUESTION_BY_KEY } from './searchModel';

/** How many "Try" suggestions the empty bar offers. */
const MAX_EXAMPLES = 3;

export interface QueryBarProps {
  /** Runs the parse and applies it to the page's filters. */
  onSubmit: (text: string) => ParsedQuery | null;
  /** The last parse, so the bar can report what it read. Null once the filters diverge from it. */
  parsed: ParsedQuery | null;
  /** Clears both the text and the parse report. */
  onClear: () => void;
  /**
   * One ready-worded example built from THIS estate's data — a registered name.
   *
   * The whole SENTENCE, not the name: how to word it depends on the page ("where was Asha
   * today" is a WHICH-AREAS question, which Tracking does not answer), and the page is what
   * knows. `nameExampleFor` in the parser builds it, beside the lists it has to agree with.
   *
   * A real name rather than a placeholder, because an example is a button: "where was <name>
   * today" would parse the angle brackets as an unread word and teach the operator that names
   * do not work. Null when the registry is empty or cannot be listed, so the page never
   * advertises a capability this user does not have.
   */
  nameExample?: string | null;
  /**
   * The worked examples under the bar. Defaults to the search set; Tracking passes its own,
   * because an example that reaches an answer this page does not give teaches the opposite of
   * what it is for.
   */
  examples?: string[];
  /**
   * Labelled rows of suggestions ("Number", "Text"), shown INSTEAD of `examples` when given.
   * Every item is shown — they are short words, not sentences, so they fit the column.
   */
  suggestionGroups?: Array<{ label: string; items: string[] }> | null;
  /** The prompt in the empty box. Same reason as `examples`. */
  placeholder?: string;
  /**
   * A sentence to start with — the host's `?q=`, which is how the "answered on the other page"
   * notices carry a query across. Applied like a typed one, once `ready`.
   */
  initialText?: string;
  /**
   * Hold the automatic apply until the page's vocabulary has loaded. Default true.
   *
   * The parse resolves camera names, groups and attribute values against that vocabulary, so
   * running it early yields a sentence with half its filters silently missing — and with no
   * submit button in the flow, nothing would ever re-run it.
   */
  ready?: boolean;
}

/**
 * How long a pause means "finished typing".
 *
 * Long enough that an ordinary inter-word gap does not fire a query, short enough that stopping
 * to read the controls does not feel like waiting for them. Every fire is a real search, the
 * same cost as clicking a filter chip, which the page has always done immediately.
 */
const APPLY_AFTER_MS = 500;

export default function QueryBar({ onSubmit, parsed, onClear, nameExample,
  examples = QUERY_EXAMPLES,
  suggestionGroups = null,
  placeholder = 'Ask in words — “red shirt person visited which areas”',
  initialText = '',
  ready = true,
}: QueryBarProps) {
  const [text, setText] = useState(initialText);

  /**
   * The sentence currently in force below.
   *
   * A ref and not state: it is a record of what was already sent, read only to decide whether
   * to send again, and re-rendering on it would be a render per keystroke for nothing. It is
   * what stops an explicit Ask and the pending pause from applying the same text twice, and
   * what stops a change of `onSubmit`'s identity from re-applying a sentence on its own.
   */
  const applied = useRef<string | null>(null);

  /**
   * The box says something the controls below have not been told yet.
   *
   * Shown, because the half-second the pause costs is exactly the window in which the "Read as"
   * row is describing the PREVIOUS sentence — which is the out-of-sync state this whole change
   * exists to remove, just a shorter one. A word saying so is the difference between a page
   * that lags and a page that is working.
   */
  const [pending, setPending] = useState(false);

  // Held in refs so the timer below depends on the TEXT alone. `onSubmit` is rebuilt whenever
  // the page's filters change — which this very call causes — and depending on it directly
  // would re-arm the timer from its own result.
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;
  const clearRef = useRef(onClear);
  clearRef.current = onClear;

  const apply = (value: string) => {
    const trimmed = value.trim();
    applied.current = trimmed;
    setPending(false);
    if (trimmed) submitRef.current(trimmed);
    else clearRef.current();
  };

  useEffect(() => {
    if (!ready) return undefined;
    const trimmed = text.trim();
    // Already the live query — an Ask, an example, or the pause that beat this one.
    if (trimmed === (applied.current ?? '')) { setPending(false); return undefined; }
    // Nothing was ever applied and there is nothing to apply: the first render of an empty bar
    // must not call `onClear`, which would reset filters an operator had set by hand.
    if (!trimmed && applied.current === null) return undefined;
    setPending(true);
    const timer = setTimeout(() => {
      // Enter / Ask may have applied this exact text while the pause was running. Applying it
      // twice re-ran the query, and could undo a filter picked in between the two.
      if (applied.current === trimmed) { setPending(false); return; }
      apply(trimmed);
    }, APPLY_AFTER_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, ready]);

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setText(trimmed);
    apply(trimmed);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(text);
  };

  const clear = () => {
    setText('');
    applied.current = '';
    onClear();
  };

  return (
    <div className="shrink-0 border-b border-border bg-card px-4 py-2.5">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            aria-label="Search in words"
            className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          />
          {text && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear the query"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {/* Kept although the sentence applies on its own: a pause is a GUESS about when a
            thought finished, and it is wrong often enough that removing the button would leave
            the page feeling like it was waiting for nothing. Secondary styling, because it is
            now an accelerator rather than the way the feature is used. */}
        <button
          type="submit"
          disabled={!text.trim()}
          title="Apply now — the sentence applies on its own after a short pause"
          className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-40"
        >
          Ask
        </button>
      </form>

      {/* The examples ARE the documentation. Nobody reads a help panel about a search box, and
          each of these reaches a different answer shape — which is the thing an operator has to
          find out exists at all. Shown only while the bar is empty, so they teach and then get
          out of the way. */}
      {!text && !parsed && !pending && suggestionGroups && suggestionGroups.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {suggestionGroups.map((g) => (
            <div key={g.label} className="flex flex-wrap items-center gap-1.5">
              <span className="w-12 shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">{g.label}</span>
              {g.items.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => submit(item)}
                  className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {item}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {!text && !parsed && !pending && !(suggestionGroups && suggestionGroups.length > 0) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Try</span>
          {[
            // Three at most: the bar sits in a narrow side column, and a wall of chips there
            // pushes the filters out of sight. The name example keeps its place as the third.
            ...examples.slice(0, nameExample ? MAX_EXAMPLES - 1 : MAX_EXAMPLES),
            // Last, not first: it is the only example whose words are specific to this estate.
            ...(nameExample ? [nameExample] : []),
          ].map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => submit(example)}
              className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {example}
            </button>
          ))}
        </div>
      )}

      {/* The pause, named. Replaces the report rather than sitting beside it: what is on screen
          during those milliseconds belongs to the OLD sentence, and leaving it up under a new
          one is the disagreement this is here to prevent. */}
      {pending && (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
          <SparklesIcon className="h-3.5 w-3.5 animate-pulse" />
          Reading “{text.trim()}”…
        </div>
      )}

      {parsed && !pending && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          <span className="flex items-center gap-1 text-muted-foreground">
            <SparklesIcon className="h-3.5 w-3.5" />
            Read as
          </span>
          <span className="rounded-full bg-brand-light px-2 py-0.5 font-medium text-foreground">
            {QUESTION_BY_KEY[parsed.question]?.label || parsed.question}
          </span>

          {/* Every spelling fix, named both ways. */}
          {parsed.corrections.map(([typed, readAs]) => (
            <span key={`${typed}-${readAs}`} className="text-muted-foreground">
              <span className="line-through">{typed}</span>
              {' → '}
              <strong className="font-medium text-foreground">{readAs}</strong>
            </span>
          ))}

          {parsed.notes.map((note) => (
            <span key={note} className="text-muted-foreground">{note}</span>
          ))}

          {/* Amber, not grey: this is the case where the query is narrower than the sentence, and
              the results below are a real answer to a DIFFERENT question. */}
          {parsed.unparsed.length > 0 && (
            <span className="flex items-center gap-1 text-amber-500">
              <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
              Could not read {parsed.unparsed.map((w) => `“${w}”`).join(', ')} — not a filter or a
              place on this node, so it was left out
            </span>
          )}

          <span className="ml-auto text-muted-foreground">
            Adjust anything below — the controls are the query
          </span>
        </div>
      )}
    </div>
  );
}
